// src/components/customer/CustomerShop.js
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { calculateTotalWithFee, calculateCCFee, applyTierDiscount } from '../../config/constants';
import { ShoppingCart, Plus, Minus, Trash2, MapPin, Loader2, CreditCard, FileText, Package } from 'lucide-react';

export function CustomerShop({ customer, products, onOrderPlaced }) {
  const [cart, setCart] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(
    customer?.shippingLocations?.find(l => l.isDefault) || customer?.shippingLocations?.[0] || null
  );
  const [notes, setNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('products'); // products, cart, checkout

  // Apply customer's discount to products
  const discount = customer?.tierDiscount || 0;
  const discountedProducts = products.map(p => ({
    ...p,
    originalPrice: p.wholesalePrice,
    wholesalePrice: Math.round(applyTierDiscount(p.wholesalePrice, discount) * 100) / 100
  }));

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: product.minQuantity || 1 }]);
    }
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setCart(cart.filter(item => item.id !== productId));
    } else {
      setCart(cart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + item.wholesalePrice * item.quantity, 0);
  const includesCCFee = customer?.paymentMethod === 'customerCC';
  const ccFee = includesCCFee ? calculateCCFee(subtotal) : 0;
  const total = includesCCFee ? calculateTotalWithFee(subtotal) : subtotal;

  const handlePlaceOrder = async () => {
    if (!selectedLocation) {
      setError('Please select a shipping location');
      return;
    }
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    setPlacing(true);
    setError('');

    try {
      // Prices, discount, and rep commission are all recomputed server-side —
      // the cart here is only a preview
      const placeOrderFn = httpsCallable(functions, 'placeOrder');
      const result = await placeOrderFn({
        customerId: customer.id,
        items: cart.map(item => ({ id: item.id, quantity: item.quantity })),
        shippingLocation: selectedLocation,
        notes: notes
      });

      // Reset
      setCart([]);
      setNotes('');
      setView('products');

      if (onOrderPlaced) {
        onOrderPlaced(result.data.orderId);
      }

      alert('Order placed successfully!');
    } catch (err) {
      console.error('Error placing order:', err);
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  // Product Grid View
  if (view === 'products') {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Shop Products</h2>
            <p className="text-sm text-green-600">Your discount: {discount}% off all products</p>
          </div>
          <button
            onClick={() => setView('cart')}
            className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          >
            <ShoppingCart size={18} />
            Cart ({itemCount})
            {itemCount > 0 && <span className="font-bold">${subtotal.toFixed(2)}</span>}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {discountedProducts.map(product => (
            <div key={product.id} className="bg-white rounded-lg shadow overflow-hidden">
              {product.image && (
                <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
              )}
              <div className="p-4">
                <h3 className="font-semibold">{product.name}</h3>
                {product.description && (
                  <p className="text-sm text-gray-500 mt-1">{product.description}</p>
                )}
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-xl font-bold text-green-600">${product.wholesalePrice.toFixed(2)}</span>
                  {product.originalPrice !== product.wholesalePrice && (
                    <span className="text-sm text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
                  )}
                </div>
                {product.minQuantity > 1 && (
                  <p className="text-xs text-gray-500 mt-1">Min order: {product.minQuantity}</p>
                )}
                <button
                  onClick={() => addToCart(product)}
                  className="w-full mt-3 bg-black text-white py-2 rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2"
                >
                  <Plus size={16} /> Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>

        {discountedProducts.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No products available</p>
          </div>
        )}
      </div>
    );
  }

  // Cart / Checkout View
  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => setView('products')}
          className="text-gray-500 hover:text-black"
        >
          ← Back to Products
        </button>
        <h2 className="text-2xl font-bold">
          {view === 'cart' ? 'Your Cart' : 'Checkout'}
        </h2>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          {cart.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <ShoppingCart size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Your cart is empty</p>
              <button
                onClick={() => setView('products')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              {cart.map(item => (
                <div key={item.id} className="p-4 border-b last:border-b-0 flex items-center gap-4">
                  {item.image && (
                    <img src={item.image} alt="" className="w-16 h-16 object-cover rounded" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-green-600">${item.wholesalePrice.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded hover:bg-gray-200"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-12 text-center font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded hover:bg-gray-200"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="w-24 text-right">
                    <p className="font-bold">${(item.wholesalePrice * item.quantity).toFixed(2)}</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:bg-red-50 p-2 rounded"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Shipping Location */}
          {cart.length > 0 && view === 'checkout' && customer?.shippingLocations?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin size={18} /> Shipping Location
              </h3>
              <div className="space-y-2">
                {customer.shippingLocations.map((location, index) => (
                  <label
                    key={index}
                    className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedLocation === location
                        ? 'border-black bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="shippingLocation"
                        checked={selectedLocation === location}
                        onChange={() => setSelectedLocation(location)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium">
                          {location.name}
                          {location.isDefault && <span className="text-green-600 text-sm ml-2">(Default)</span>}
                        </p>
                        <p className="text-sm text-gray-600">{location.street}</p>
                        <p className="text-sm text-gray-600">{location.city}, {location.state} {location.zip}</p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Order Notes */}
          {cart.length > 0 && view === 'checkout' && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="font-semibold mb-4">Order Notes (Optional)</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Special instructions, delivery notes, etc."
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-4">
            <h3 className="font-semibold mb-4">Order Summary</h3>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span>Subtotal ({itemCount} items)</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600">
                <span>Your Discount ({discount}%)</span>
                <span>Applied</span>
              </div>
              {includesCCFee && (
                <div className="flex justify-between text-sm text-gray-500">
                  <span>CC Processing Fee</span>
                  <span>${ccFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Method Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium mb-1">Payment Method</p>
              <p className="flex items-center gap-2 text-gray-600">
                {customer?.paymentMethod === 'customerCC' && <><CreditCard size={14} /> Credit Card at Checkout</>}
                {customer?.paymentMethod === 'customerInvoice' && <><FileText size={14} /> Invoice (Net 30)</>}
              </p>
            </div>

            {view === 'cart' ? (
              <button
                onClick={() => setView('checkout')}
                disabled={cart.length === 0}
                className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400"
              >
                Proceed to Checkout
              </button>
            ) : (
              <button
                onClick={handlePlaceOrder}
                disabled={placing || cart.length === 0 || !selectedLocation}
                className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                {placing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Placing Order...
                  </>
                ) : (
                  'Place Order'
                )}
              </button>
            )}

            {customer?.paymentMethod === 'customerInvoice' && view === 'checkout' && (
              <p className="text-xs text-gray-500 text-center mt-2">
                You will receive an invoice for this order
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
